import React, { useState, useEffect, useMemo } from "react";
import {
  Table, Container, Row, Col, Form, Button, ButtonGroup,
  OverlayTrigger, Tooltip, Modal, Offcanvas, Accordion, Badge, Spinner
} from "react-bootstrap";
import { getUserPracticeById } from "../../api";
import { getAgedInvoicesByPracticeNumbers } from "../../afinanceapi";
import { jwtDecode } from "jwt-decode";
import { useLocation, useNavigate } from "react-router-dom";
import { unparse } from "papaparse";
import "./CreateInvoice.css"; // Assuming you have some styles in this file

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function AgedInvoicesList() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Data for dropdowns
  const [practiceList, setPracticeList] = useState([]);

  // Committed filters (drive the table)
  const [selectedPractices, setSelectedPractices] = useState([]); // [] = All
  const [selectedStatuses, setSelectedStatuses] = useState([]);   // [] = All
  const [selectedAging, setSelectedAging] = useState([]);         // [], e.g. ["30","60"]

  // Committed dates (default 1 month ago → today)
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Temporary filters while panel is open
  const [tmpPractices, setTmpPractices] = useState([]);
  const [tmpStatuses, setTmpStatuses] = useState([]);
  const [tmpAging, setTmpAging] = useState([]);
  const [tmpStartDate, setTmpStartDate] = useState("");
  const [tmpEndDate, setTmpEndDate] = useState("");
  const [userRole, setUserRole] = useState(false); // True if user has allowed role

  // Pagination
  const [page, setPage] = useState(1);
  const limit = 20;

  // UI
  const [showInfo, setShowInfo] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const navigate = useNavigate();
  const query = useQuery();
  const agingParam = query.get("aging");

  const currency = new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR" });

  // Init dates once
useEffect(() => {
  const today = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 1); // Default to last 1 month
  const iso = (d) => d.toISOString().slice(0, 10);
  setStartDate(iso(start));
  setEndDate(iso(today));
  setTmpStartDate(iso(start));
  setTmpEndDate(iso(today));
}, []);

  // Fetch data
  useEffect(() => {
    async function fetchInvoices() {
      try {
        setLoading(true);
        const token = sessionStorage.getItem("user");
        if (!token) throw new Error("No user token found");
        const user = jwtDecode(token);
        const rolesFromRole = Array.isArray(user?.role)
      ? user.role
      : user?.role
      ? [user.role]
      : [];
    const rolesFromPermissions = Array.isArray(user?.permissions)
      ? user.permissions
      : [];
    const roles = [...new Set([...rolesFromRole, ...rolesFromPermissions])];

    const allowedRoles = ["Practitioner", "Finance", "SuperAdmin"];
    const hasAllowedRole = roles.some((r) => allowedRoles.includes(r));
    setUserRole(hasAllowedRole);
        const practices = await getUserPracticeById(user._id);
        setPracticeList(practices || []);
        const data = await getAgedInvoicesByPracticeNumbers(user.practice);
        const withTotals = (data || []).map((inv) => ({
          ...inv,
          total:
            (inv.days30 || 0) +
            (inv.days60 || 0) +
            (inv.days90 || 0) +
            (inv.days120plus || 0),
        }));
        setInvoices(withTotals);
      } catch (e) {
        console.error("Error fetching aged invoices:", e);
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    }
    fetchInvoices();
  }, []);

  // Apply ?aging= once (now multi-select -> seed it if valid)
  useEffect(() => {
    if (agingParam && [30, 60, 90, 120].includes(parseInt(agingParam))) {
      const val = String(parseInt(agingParam));
      setSelectedAging([val]);
      setTmpAging([val]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agingParam]);

  // Dynamic statuses (always include "Pending")
  const statusOptions = useMemo(() => {
    const set = new Set(invoices.map((i) => i.status).filter(Boolean));
    set.add("Pending"); // Always include Pending as a filter option
    return Array.from(set).sort();
  }, [invoices]);

  // Helpers for multi-select toggles
  const toggleIn = (arr, value) =>
    arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

  const allPractices = useMemo(
    () => (practiceList || []).map((p) => p.practiceName).filter(Boolean),
    [practiceList]
  );

  // Filtered (entire dataset)
  const filteredInvoices = useMemo(() => {
  return invoices.filter((inv) => {
    const invDate = inv.invoiceDate ? new Date(inv.invoiceDate) : null;

    // ✅ inclusive boundaries in local time
    const from = startDate ? new Date(`${startDate}T00:00:00.000`) : null;
    const to   = endDate   ? new Date(`${endDate}T23:59:59.999`) : null;

    // Include Pending invoices without dates, or check date boundaries
    const fromOK = !from || !invDate || invDate >= from;
    const toOK   = !to   || !invDate || invDate <= to;

      const practiceName = inv.practice?.practiceName;
      const practiceOK =
        selectedPractices.length === 0 || (practiceName && selectedPractices.includes(practiceName));

      const statusOK =
        selectedStatuses.length === 0 || (inv.status && selectedStatuses.includes(inv.status));

      const has30 = (inv.days30 || 0) > 0;
      const has60 = (inv.days60 || 0) > 0;
      const has90 = (inv.days90 || 0) > 0;
      const has120 = (inv.days120plus || 0) > 0;

      // Include Pending invoices regardless of aging filter
      const agingOK =
        selectedAging.length === 0 ||
        inv.status === "Pending" ||
        selectedAging.some((a) =>
          (a === "30" && has30) ||
          (a === "60" && has60) ||
          (a === "90" && has90) ||
          (a === "120" && has120)
        );

      return fromOK && toOK && practiceOK && statusOK && agingOK;
    });
  }, [invoices, selectedPractices, selectedStatuses, selectedAging, startDate, endDate]);

  // Sort the filtered set (then paginate)
  const sortedFilteredInvoices = useMemo(() => {
    if (!sortConfig.key) return filteredInvoices;
    const arr = [...filteredInvoices];
    arr.sort((a, b) => {
      const valA = a[sortConfig.key] ?? "";
      const valB = b[sortConfig.key] ?? "";
      if (typeof valA === "string") {
        return sortConfig.direction === "asc"
          ? String(valA).localeCompare(String(valB))
          : String(valB).localeCompare(String(valA));
      }
      if (typeof valA === "number") {
        return sortConfig.direction === "asc" ? valA - valB : valB - valA;
      }
      return 0;
    });
    return arr;
  }, [filteredInvoices, sortConfig]);

  // Totals for entire filtered dataset
  const totals = useMemo(
    () => ({
      sum30: filteredInvoices.reduce((s, x) => s + (x.days30 || 0), 0),
      sum60: filteredInvoices.reduce((s, x) => s + (x.days60 || 0), 0),
      sum90: filteredInvoices.reduce((s, x) => s + (x.days90 || 0), 0),
      sum120: filteredInvoices.reduce((s, x) => s + (x.days120plus || 0), 0),
      sumAll: filteredInvoices.reduce((s, x) => s + (x.total || 0), 0),
    }),
    [filteredInvoices]
  );

  // Pagination
  const totalPages = Math.max(1, Math.ceil(sortedFilteredInvoices.length / limit));
  const pageStart = (page - 1) * limit;
  const pagedInvoices = sortedFilteredInvoices.slice(pageStart, pageStart + limit);

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [selectedPractices, selectedStatuses, selectedAging, startDate, endDate]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key)
      return <i className="fa-solid fa-arrows-up-down text-primary ms-2" />;
    return sortConfig.direction === "asc" ? (
      <i className="fa-solid fa-arrow-up text-primary ms-2" />
    ) : (
      <i className="fa-solid fa-arrow-down text-primary ms-2" />
    );
  };

  const handleExportCSV = () => {
    const exportData = filteredInvoices.map((inv) => ({
      InvoiceDate: inv.invoiceDate,
      InvoiceNumber: inv.invoiceNumber,
      PracticeName: inv.practice?.practiceName || "",
      AccountName: inv.accountName,
      Status: inv.status,
      Days30: inv.days30 || 0,
      Days60: inv.days60 || 0,
      Days90: inv.days90 || 0,
      Days120plus: inv.days120plus || 0,
      Total: inv.total || 0,
    }));
    const csv = unparse(exportData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "aged-invoices.csv";
    a.click();
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: "Aged Invoices", url });
      else {
        await navigator.clipboard.writeText(url);
        alert("Link copied to clipboard.");
      }
    } catch {}
  };

  // Open filter panel with current committed values loaded into temp
  const openFilters = () => {
    setTmpPractices(selectedPractices);
    setTmpStatuses(selectedStatuses);
    setTmpAging(selectedAging);
    setTmpStartDate(startDate);
    setTmpEndDate(endDate);
    setShowFilters(true);
  };

  const applyFilters = () => {
    setSelectedPractices(tmpPractices);
    setSelectedStatuses(tmpStatuses);
    setSelectedAging(tmpAging);
    setStartDate(tmpStartDate);
    setEndDate(tmpEndDate);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setSelectedPractices([]);
    setSelectedStatuses([]);
    setSelectedAging([]);
    // Reset to default date range (last month → today)
    const today = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    const iso = (d) => d.toISOString().slice(0, 10);
    const s = iso(start);
    const e = iso(today);
    setStartDate(s);
    setEndDate(e);

    // Also reset temp in case panel is open
    setTmpPractices([]);
    setTmpStatuses([]);
    setTmpAging([]);
    setTmpStartDate(s);
    setTmpEndDate(e);
  };

  const activeCount =
    (selectedPractices.length ? 1 : 0) +
    (selectedStatuses.length ? 1 : 0) +
    (selectedAging.length ? 1 : 0) +
    ((startDate && endDate) ? 1 : 0);

  return (
  <Container fluid className="pt-2">
    {/* Single top bar: Filter + Remove + Pagination + CSV + Share/Info (right-aligned) */}
    <Row className="mb-3 align-items-center">
      {/* Right Column: Totals Table */}
  <Col md={5}>
    <Table bordered size="sm" className="mb-0 text-center align-middle">
      <thead className="table-primary">
        <tr>
          <th style={{ width: "20%" }} className="fw-semibold text-primary">30 days</th>
          <th style={{ width: "20%" }} className="fw-semibold text-primary">60 days</th>
          <th style={{ width: "20%" }} className="fw-semibold text-primary">90 days</th>
          <th style={{ width: "20%" }} className="fw-semibold text-primary">120 days+</th>
          <th style={{ width: "20%" }} className="fw-semibold text-primary">Total</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>{currency.format(totals.sum30)}</td>
          <td>{currency.format(totals.sum60)}</td>
          <td>{currency.format(totals.sum90)}</td>
          <td>{currency.format(totals.sum120)}</td>
          <td><strong>{currency.format(totals.sumAll)}</strong></td>
        </tr>
      </tbody>
    </Table>
  </Col>
  {/* Left Column: Filter + Pagination + Actions */}
  <Col md={7} className="d-flex gap-2 flex-wrap justify-content-end">
    <Button variant="primary" onClick={openFilters} style={{ width: "120px" }}>
      <i className="fa-solid fa-filter me-2" />
      Filter {activeCount > 0 && <Badge className="ms-2" bg="light" text="dark">{activeCount}</Badge>}
    </Button>
    <Button
      variant="outline-danger"
      onClick={clearFilters}
      style={{ width: "120px" }}
      disabled={
        selectedPractices.length === 0 &&
        selectedStatuses.length === 0 &&
        selectedAging.length === 0 &&
        !startDate &&
        !endDate
      }
    >
      <i className="fa-solid fa-trash me-2" />
      Filters
    </Button>

    {/* Pagination */}
    <div className="d-flex align-items-center gap-2 ms-2">
      <Button
        variant="primary"
        disabled={page === 1}
        onClick={() => setPage((p) => Math.max(1, p - 1))}
      >
        <i className="fa-solid fa-angle-left" />
      </Button>
      <div className="text-nowrap small">
        Page {page} of {totalPages}
      </div>
      <Button
        variant="primary"
        disabled={page === totalPages}
        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
      >
        <i className="fa-solid fa-angle-right" />
      </Button>
      
    </div>

    {/* Share + Info */}
    <ButtonGroup className="ms-1 gap-1">
      <OverlayTrigger placement="bottom" overlay={<Tooltip>Export to CSV</Tooltip>}>
      <Button variant="outline-secondary" onClick={handleExportCSV}>
        <i className="fa-solid fa-file-export" />
      </Button>
      </OverlayTrigger>
      
      <OverlayTrigger placement="bottom" overlay={<Tooltip>Info</Tooltip>}>
        <Button variant="outline-secondary" onClick={() => setShowInfo(true)}>
          <i className="fa-solid fa-circle-info" />
        </Button>
      </OverlayTrigger>
    </ButtonGroup>
  </Col>

  
</Row>


    {loading ? (
      <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading Invoices...</p>
          </div>
    ) : pagedInvoices.length === 0 ? (
      <p>No invoices found.</p>
    ) : (
      <Table bordered hover responsive className="fixed-table">
        <thead className="table-primary text-center align-middle ms-1 me-1">
          <tr>
            <th style={{ width: "2%" }} className="text-primary fw-semibold">#</th>
            <th style={{ width: "8%" }} className="text-primary fw-semibold">Date</th>
            <th style={{ width: "10%" }} className="text-primary fw-semibold">Inv Number</th>
            <th style={{ width: "18%" }} className="text-primary fw-semibold">Practice Name</th>
            <th
              style={{ cursor: "pointer", width: "18%" }}
              onClick={() => handleSort("accountName")}
              className="text-primary fw-semibold"
            >
              Account Name {renderSortIcon("accountName")}
            </th>
            <th
              style={{ cursor: "pointer", width: "10%" }}
              onClick={() => handleSort("status")}
              className="text-primary fw-semibold"
            >
              Status {renderSortIcon("status")}
            </th>
            <th
              style={{ cursor: "pointer", width: "9%" }}
              onClick={() => handleSort("days30")}
              className="text-primary fw-semibold"
            >
              30 Days {renderSortIcon("days30")}
            </th>
            <th
              style={{ cursor: "pointer", width: "9%" }}
              onClick={() => handleSort("days60")}
              className="text-primary fw-semibold"
            >
              60 Days {renderSortIcon("days60")}
            </th>
            <th
              style={{ cursor: "pointer", width: "9%" }}
              onClick={() => handleSort("days90")}
              className="text-primary fw-semibold"
            >
              90 Days {renderSortIcon("days90")}
            </th>
            <th
              style={{ cursor: "pointer", width: "9%" }}
              onClick={() => handleSort("days120plus")}
              className="text-primary fw-semibold"
            >
              120 Days {renderSortIcon("days120plus")}
            </th>
            <th
              style={{ cursor: "pointer", width: "10%" }}
              onClick={() => handleSort("total")}
              className="text-primary fw-semibold"
            >
              Total {renderSortIcon("total")}
            </th>
          </tr>
        </thead>
        <tbody>
          {pagedInvoices.map((inv, idx) => (
            <tr
              key={inv._id}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#6c757d")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
            >
              <td>{(page - 1) * limit + idx + 1}</td>
              <td>{inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString() : ""}</td>
              <td
                style={{ cursor: "pointer" }}
                onClick={() =>
                  navigate(
                    inv.status === "Pending"
                      ? `/invoices/${inv._id}/${inv.accountId}`
                      : `/invoices/posted/${inv._id}`
                  )
                }
              >
                {inv.invoiceNumber}
              </td>
              <td>{inv.practice?.practiceName}</td>
              <td
                style={{ cursor: "pointer" }}
                onClick={() => navigate(`/accounts/${inv.accountId}`)}
              >
                {inv.accountName}
              </td>
              <td>{inv.status}</td>
              <td className="text-end">{inv.days30 > 0 ? currency.format(inv.days30) : ""}</td>
              <td className="text-end">{inv.days60 > 0 ? currency.format(inv.days60) : ""}</td>
              <td className="text-end">{inv.days90 > 0 ? currency.format(inv.days90) : ""}</td>
              <td className="text-end">{inv.days120plus > 0 ? currency.format(inv.days120plus) : ""}</td>
              <td className="text-end fw-bold">{inv.total > 0 ? currency.format(inv.total) : ""}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    )}

    {/* Info modal */}
    <Modal show={showInfo} onHide={() => setShowInfo(false)}>
      <Modal.Header closeButton>
        <Modal.Title>Aged Invoices</Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-muted">
        Use the filter panel to refine the dataset. Totals reflect all filtered rows (not just this page).
        CSV export downloads the entire filtered dataset.
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={() => setShowInfo(false)}>Got it</Button>
      </Modal.Footer>
    </Modal>

    {/* Filter Offcanvas (unchanged) */}
    <Offcanvas show={showFilters} onHide={() => setShowFilters(false)} placement="start">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title className="test-primary d-flex align-items-center">
          <i className="fa-solid fa-filter me-2 text-primary" />
          Filters
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {/* Dates */}
        <div className="mb-3">
          <h6 className="mb-1 text-primary fw-normal ms-3 mb-2">Date range</h6>
          <div className="d-flex gap-2 ms-1">
            <Form.Control
              type="date"
              value={tmpStartDate}
              onChange={(e) => setTmpStartDate(e.target.value)}
            />
            <Form.Control
              type="date"
              value={tmpEndDate}
              onChange={(e) => setTmpEndDate(e.target.value)}
            />
          </div>
        </div>

        <Accordion alwaysOpen>
          {/* Practices */}
          <Accordion.Item eventKey="0">
            <Accordion.Header>Practices</Accordion.Header>
            <Accordion.Body>
              <div className="mb-2 d-flex gap-2">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setTmpPractices(allPractices)}
                >
                  Select all
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setTmpPractices([])}
                >
                  Clear
                </Button>
              </div>
              <div className="d-flex flex-column" style={{ maxHeight: 240, overflowY: "auto" }}>
                {allPractices.map((name) => (
                  <Form.Check
                    key={name}
                    type="checkbox"
                    id={`practice-${name}`}
                    label={name}
                    checked={tmpPractices.includes(name)}
                    onChange={() => setTmpPractices((arr) => toggleIn(arr, name))}
                  />
                ))}
              </div>
            </Accordion.Body>
          </Accordion.Item>

          {/* Statuses */}
          <Accordion.Item eventKey="1">
            <Accordion.Header>Status</Accordion.Header>
            <Accordion.Body>
              <div className="mb-2 d-flex gap-2">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setTmpStatuses(statusOptions)}
                >
                  Select all
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setTmpStatuses([])}
                >
                  Clear
                </Button>
              </div>
              <div className="d-flex flex-column">
                {statusOptions.map((s) => (
                  <Form.Check
                    key={s}
                    type="checkbox"
                    id={`status-${s}`}
                    label={s}
                    checked={tmpStatuses.includes(s)}
                    onChange={() => setTmpStatuses((arr) => toggleIn(arr, s))}
                  />
                ))}
              </div>
            </Accordion.Body>
          </Accordion.Item>

          {/* Aging */}
          <Accordion.Item eventKey="2">
            <Accordion.Header>Aging</Accordion.Header>
            <Accordion.Body>
              <div className="d-flex flex-column">
                {["30", "60", "90", "120"].map((a) => (
                  <Form.Check
                    key={a}
                    type="checkbox"
                    id={`aging-${a}`}
                    label={`${a} Days${a === "120" ? "+" : ""}`}
                    checked={tmpAging.includes(a)}
                    onChange={() => setTmpAging((arr) => toggleIn(arr, a))}
                  />
                ))}
              </div>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>

        {/* Actions */}
        <div className="mt-4 d-flex gap-2">
          <Button onClick={applyFilters} style={{ width: "120px" }}>
            <i className="fa-solid fa-filter me-2 text-white" />
            Apply
          </Button>
          <Button
            style={{ width: "120px" }}
            variant="outline-danger"
            onClick={() => {
              setTmpPractices([]);
              setTmpStatuses([]);
              setTmpAging([]);
              setTmpStartDate("");
              setTmpEndDate("");
            }}
          >
            <i className="fa-solid fa-trash me-2" />
            Remove
          </Button>
        </div>
        <div className="mt-2 text-muted small">
          Tip: leaving a section empty means “All”.
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  </Container>
);

}
